import { get, post, put } from '@/js/api/sourcer';
import STATUS from '@/store/status';
import Vue from 'vue';
import Config from '@/js/config';
import { withUserLoaded } from '@/store/util';

const SET_SETTINGS = 'SET_SETTINGS';
const SET_STATUS = 'SET_STATUS';
const UPDATE_CALIBRATE = 'UPDATE_CALIBRATE';

const State = {
  settings: {},
  status: {},
};

// getters
const Getters = {
  getSettingsByProjectId: (state, getters) => (projectId) => {
    if (
      getters.getStatus(projectId) === STATUS.LOADED ||
      getters.getStatus(projectId) === STATUS.STALE
    ) {
      return state.settings[projectId];
    }
    return {};
  },

  getStatus: (state) => (projectId) =>
    state.status[projectId] || STATUS.NOT_LOADED,
};

// mutations
const Mutations = {
  [SET_SETTINGS](state, { projectId, settings }) {
    Vue.set(state.settings, projectId, {
      ...state.settings[projectId],
      ...settings,
    });
    Vue.set(state.status, projectId, STATUS.LOADED);
  },
  [SET_STATUS](state, { projectId, status }) {
    Vue.set(state.status, projectId, status);
  },
};

// actions
const Actions = {
  loadSettings: withUserLoaded(async (context, { projectId }) => {
    if (!projectId) {
      return undefined;
    }

    const status = context.getters.getStatus(projectId);
    if (status === STATUS.LOADING || status === STATUS.LOADED) {
      // Already loading or loaded. Do nothing.
      return undefined;
    }

    context.commit(SET_STATUS, { projectId, status: STATUS.LOADING });
    try {
      const url = context.rootGetters['user/getIsUserV2']
        ? Config.env.API_URL_V2
        : Config.env.API_URL;
      const response = await get(
        `${url}projects/settings?project_id=${projectId}`
      );
      if (response && response.status === 200) {
        context.commit(SET_SETTINGS, { projectId, settings: response.data });
        context.commit(SET_STATUS, { projectId, status: STATUS.LOADED });
        return response;
      }
      context.commit(SET_SETTINGS, { projectId, settings: {} });
      context.commit(SET_STATUS, { projectId, status: STATUS.ERROR });
      return null;
    } catch (e) {
      console.log(e);
      context.commit(SET_SETTINGS, { projectId, settings: {} });
      context.commit(SET_STATUS, { projectId, status: STATUS.ERROR });
      return null;
    }
  }),
  // TODO(jeremiah): deprecate after v2 rollout
  async createProject(context, { project }) {
    const response = await post(`${Config.env.API_URL}projects`, project);
    context.commit(`projects/${SET_STATUS}`, STATUS.STALE, { root: true });
    return response;
  },
  async createOrUpdateV2Project(context, { project }) {
    const response = await post(`${Config.env.API_URL_V2}projects`, project);
    context.commit(`projects/${SET_STATUS}`, STATUS.STALE, { root: true });
    return response;
  },
  async updateV2Flashcards(context, { project }) {
    const response = await post(
      `${Config.env.API_URL_V2}projects/flashcards`,
      project
    );
    context.commit(`projects/${SET_STATUS}`, STATUS.STALE, { root: true });
    return response;
  },
  async updateV2ProjectSettings(context, { project }) {
    const response = await post(
      `${Config.env.API_URL_V2}projects/settings`,
      project
    );
    context.commit(`projects/${SET_STATUS}`, STATUS.STALE, { root: true });
    return response;
  },
  // TODO(jeremiah): deprecate after v2 rollout
  async createSelfServeProject(context, { project }) {
    const response = await post(
      `${Config.env.API_URL}projects/self_serve`,
      project
    );
    context.commit(`projects/${SET_STATUS}`, STATUS.STALE, { root: true });
    return response;
  },
  updateProjectFE(context, { projectId, project }) {
    context.commit(SET_SETTINGS, { projectId, settings: project });
  },

  async updateProject(
    context,
    { projectId, project, outreachParamTmp, settingsOnly }
  ) {
    if (
      project.is_in_auto_swipe_calibration !== undefined &&
      project.is_in_auto_swipe_calibration !== null
    ) {
      context.commit(
        `projects/${UPDATE_CALIBRATE}`,
        {
          projectId,
          value: project.is_in_calibration,
        },
        { root: true }
      );
      context.commit(SET_SETTINGS, { projectId, settings: project });
    }
    const response = await put(
      `${Config.env.API_URL}projects/settings?project_id=${projectId}`,
      project
    );

    if (response && response.status === 200) {
      if (!settingsOnly) {
        context.commit(
          'projects/SET_PROJECT',
          { projectId, project },
          { root: true }
        );
      }
      const projectParam = project;
      const outreachParams = outreachParamTmp;
      if (project.template_vars_dict) {
        const arr = Object.keys(project.template_vars_dict).map((key) => [
          key,
          project.template_vars_dict[key],
        ]);
        arr.forEach((e) => {
          outreachParams[e[0]][0] = e[1];
        });
        delete projectParam.template_vars_dict;
        projectParam.template_vars_dict = outreachParams;
      }
      context.commit(SET_SETTINGS, { projectId, settings: projectParam });
    }

    context.commit(`projects/${SET_STATUS}`, STATUS.STALE, { root: true });
    const tabs = [
      'UNDER_REVIEW',
      'APPROVED',
      'CONTACTED',
      'INTERVIEWING',
      'REJECTED',
      'WARM_LEADS_RECRUITER',
      'WARM_LEADS_QUESTION',
      'WARM_LEADS_HM',
      'WARM_LEADS_HM_APPROVED',
    ];
    // Mark all tabs as stale.
    tabs.forEach((tab) => {
      context.commit(
        `projects/candidates/${SET_STATUS}`,
        {
          projectId,
          tab,
          status: STATUS.STALE,
        },
        { root: true }
      );
    });

    return response;
  },
  async updateProjectSettings(context, { projectId, settings }) {
    context.commit(SET_SETTINGS, { projectId, settings });
  },
  async pauseProject(context, { projectId, shouldPause }) {
    const res = await post(`${Config.env.API_URL_V2}projects/pause`, {
      project_id: projectId,
      should_pause: shouldPause,
    });
    context.commit(
      `projects/SET_PROJECT`,
      {
        projectId,
        project: { outreach_pause: shouldPause },
      },
      { root: true }
    );
    return res;
  },
  async archiveProject(context, { projectId, shouldArchive }) {
    const res = await post(`${Config.env.API_URL_V2}projects/archive`, {
      project_id: projectId,
      should_archive: shouldArchive,
    });
    context.commit(
      `projects/SET_PROJECT`,
      {
        projectId,
        project: { is_archived: shouldArchive },
      },
      { root: true }
    );
    return res;
  },
};

export default {
  namespaced: true,
  state: State,
  getters: Getters,
  actions: Actions,
  mutations: Mutations,
};
